/**
 * Fermentation Merged Variant Picker
 * Coordinates between carousel and custom picker views.
 */

(function() {
  'use strict';

  const Shared = window.FermentationPickerShared;
  if (!Shared) return;
  const DEFAULT_CUSTOM_SKU = 'FERMSET_4BBCC_T1';

  const getScopeRoot = Shared.getScopeRoot;
  const getMergedContainer = function(root) {
    const scope = root || getScopeRoot();
    return scope.querySelector('.fermentation-merged-picker');
  };

  window.fermentationMergedPicker = {
    _initialisedContainers: null,
    _carouselAssetsPromise: null,
    _pendingCustomInitTimer: null,
    _pendingCustomInitTries: 0,
    _memoryTrackingBound: false,

    init: function() {
      const root = getScopeRoot();
      const container = getMergedContainer(root);
      if (!container) return;
      const ctx = { root, container };

      if (!this._initialisedContainers) this._initialisedContainers = new WeakSet();
      if (this._initialisedContainers.has(container)) return;
      this._initialisedContainers.add(container);
      this.bindMemoryTracking();

      this.intelligentInit(ctx);
      this.setupSwitching(ctx);
    },

    getContext: function() {
      const root = getScopeRoot();
      const container = getMergedContainer(root);
      return { root, container };
    },

    getCurrentVariant: function(ctx) {
      const root = ctx && ctx.root ? ctx.root : this.getContext().root;
      return Shared.getCurrentVariant(root);
    },

    getMemory: function(container) {
      if (!container) return { lastCarouselSku: '', lastCustomSku: '' };
      if (!container._fermentationPickerMemory) {
        container._fermentationPickerMemory = { lastCarouselSku: '', lastCustomSku: '' };
      }
      return container._fermentationPickerMemory;
    },

    isFixedSetSku: function(config, sku) {
      const safeSku = String(sku || '').trim();
      if (!safeSku || !config) return false;

      if (Array.isArray(window.fermentationFixedSetSkus) && window.fermentationFixedSetSkus.indexOf(safeSku) !== -1) {
        return true;
      }

      if (config.mainSetSkus && Object.values(config.mainSetSkus).some((value) => String(value || '').trim() === safeSku)) {
        return true;
      }

      if (Array.isArray(config.fixedSets) && config.fixedSets.some((set) => set && String(set.sku || '').trim() === safeSku)) {
        return true;
      }

      return false;
    },

    rememberSku: function(ctx, config, sku) {
      const safeSku = String(sku || '').trim();
      if (!ctx || !ctx.container || !config || !safeSku) return;

      const memory = this.getMemory(ctx.container);
      if (this.isFixedSetSku(config, safeSku)) {
        memory.lastCarouselSku = safeSku;
      } else {
        memory.lastCustomSku = safeSku;
      }
    },

    hasFixedSets: function(config) {
      return !!(config && Array.isArray(config.fixedSets) && config.fixedSets.length > 0);
    },

    resolveDefaultCarouselSku: function(config) {
      if (!config) return '';

      const compactSku = config.mainSetSkus && config.mainSetSkus.compact ? String(config.mainSetSkus.compact).trim() : '';
      if (compactSku && this.isFixedSetSku(config, compactSku) && Shared.isVariantExisting(config, compactSku)) return compactSku;

      const fixedSkus = (Array.isArray(config.fixedSets) ? config.fixedSets : [])
        .map((set) => String((set && set.sku) || '').trim())
        .filter(Boolean);

      const selectable = fixedSkus.find((sku) => Shared.isVariantSelectable(config, sku));
      if (selectable) return selectable;
      return fixedSkus.find((sku) => Shared.isVariantExisting(config, sku)) || '';
    },

    resolveDefaultCustomSku: function(config) {
      if (!config) return '';

      if (
        DEFAULT_CUSTOM_SKU &&
        !this.isFixedSetSku(config, DEFAULT_CUSTOM_SKU) &&
        Shared.isVariantExisting(config, DEFAULT_CUSTOM_SKU)
      ) {
        return DEFAULT_CUSTOM_SKU;
      }

      const customSkus = Object.keys(config.variants || {}).filter((sku) => !this.isFixedSetSku(config, sku));
      const selectable = customSkus.find((sku) => Shared.isVariantSelectable(config, sku));
      if (selectable) return selectable;
      return customSkus.find((sku) => Shared.isVariantExisting(config, sku)) || '';
    },

    resolveTargetCarouselSku: function(ctx, config) {
      const memory = this.getMemory(ctx && ctx.container);
      const remembered = String(memory.lastCarouselSku || '').trim();
      if (remembered && this.isFixedSetSku(config, remembered) && Shared.isVariantExisting(config, remembered)) {
        return remembered;
      }
      return this.resolveDefaultCarouselSku(config);
    },

    resolveTargetCustomSku: function(ctx, config) {
      const memory = this.getMemory(ctx && ctx.container);
      const remembered = String(memory.lastCustomSku || '').trim();
      if (remembered && !this.isFixedSetSku(config, remembered) && Shared.isVariantExisting(config, remembered)) {
        return remembered;
      }
      return this.resolveDefaultCustomSku(config);
    },

    bindMemoryTracking: function() {
      if (this._memoryTrackingBound) return;
      this._memoryTrackingBound = true;

      const syncFromCurrent = () => {
        const ctx = this.getContext();
        if (!ctx.container) return;
        const config = Shared.parseConfig(ctx.container);
        if (!config) return;

        const currentSku = Shared.getCurrentSkuFromSelect(config, ctx.root);
        if (currentSku) {
          this.rememberSku(ctx, config, currentSku);
          return;
        }

        const currentVariant = this.getCurrentVariant(ctx);
        if (currentVariant && currentVariant.sku) {
          this.rememberSku(ctx, config, currentVariant.sku);
        }
      };

      document.addEventListener('variantChange', syncFromCurrent);
      document.addEventListener('quickview:loaded', () => setTimeout(syncFromCurrent, 0));
      setTimeout(syncFromCurrent, 0);
    },

    findFixedSetByVariant: function(config, variant) {
      if (!config || !variant || !variant.sku) return null;

      if (Array.isArray(window.fermentationFixedSetSkus) && window.fermentationFixedSetSkus.indexOf(variant.sku) !== -1) {
        return { sku: variant.sku };
      }

      if (!Array.isArray(config.fixedSets)) return null;
      return config.fixedSets.find((set) => set && set.sku === variant.sku) || null;
    },

    showCustomizer: function(ctx) {
      const container = ctx && ctx.container ? ctx.container : this.getContext().container;
      if (!container) return;

      const carouselElement = container.querySelector('#carousel-picker');
      const customElement = container.querySelector('#custom-picker');
      if (!carouselElement || !customElement) return;

      carouselElement.style.display = 'none';
      customElement.style.display = 'block';
    },

    showCarousel: function(ctx) {
      const container = ctx && ctx.container ? ctx.container : this.getContext().container;
      if (!container) return;

      const carouselElement = container.querySelector('#carousel-picker');
      const customElement = container.querySelector('#custom-picker');
      if (!carouselElement || !customElement) return;

      customElement.style.display = 'none';
      carouselElement.style.display = 'block';
    },

    initializeCustom: function(ctx) {
      if (window.FermentationPicker && typeof window.FermentationPicker.init === 'function') {
        window.FermentationPicker.init(ctx && ctx.root ? ctx.root : getScopeRoot());
        this._pendingCustomInitTries = 0;
        if (this._pendingCustomInitTimer) {
          clearTimeout(this._pendingCustomInitTimer);
          this._pendingCustomInitTimer = null;
        }
        return;
      }

      if (this._pendingCustomInitTimer) return;
      if (this._pendingCustomInitTries >= 40) return;

      this._pendingCustomInitTries += 1;
      this._pendingCustomInitTimer = setTimeout(() => {
        this._pendingCustomInitTimer = null;
        this.initializeCustom(ctx);
      }, 50);
    },

    initializeCarousel: function(ctx) {
      if (window.FermentationCarouselPicker && typeof window.FermentationCarouselPicker.init === 'function') {
        window.FermentationCarouselPicker.init(ctx && ctx.root ? ctx.root : getScopeRoot());
      }
    },

    ensureCarouselAssetsLoaded: function() {
      if (this._carouselAssetsPromise) return this._carouselAssetsPromise;
      if (window.FermentationCarouselPicker) {
        this._carouselAssetsPromise = Promise.resolve();
        return this._carouselAssetsPromise;
      }

      const assets = window.fermentationCarouselAssets || {};
      const jsUrl = assets.js;
      const cssUrl = assets.css;

      this._carouselAssetsPromise = new Promise((resolve, reject) => {
        if (!jsUrl) {
          reject(new Error('Missing fermentationCarouselAssets.js'));
          return;
        }

        if (cssUrl) {
          const existingCss = document.querySelector(`link[rel="stylesheet"][href="${cssUrl}"]`);
          if (!existingCss) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = cssUrl;
            document.head.appendChild(link);
          }
        }

        const existingScript = document.querySelector(`script[src="${jsUrl}"]`);
        if (existingScript) {
          if (window.FermentationCarouselPicker) {
            resolve();
            return;
          }
          existingScript.addEventListener('load', () => window.FermentationCarouselPicker ? resolve() : reject(new Error('Carousel global missing')));
          existingScript.addEventListener('error', () => reject(new Error('Carousel script failed to load')));
          return;
        }

        const script = document.createElement('script');
        script.src = jsUrl;
        script.defer = true;
        script.onload = () => window.FermentationCarouselPicker ? resolve() : reject(new Error('Carousel global missing'));
        script.onerror = () => reject(new Error('Carousel script failed to load'));
        document.head.appendChild(script);
      });

      return this._carouselAssetsPromise;
    },

    showCarouselWithSet: function(set, ctx) {
      this.showCarousel(ctx);
      const context = ctx || this.getContext();
      const config = context && context.container ? Shared.parseConfig(context.container) : null;
      if (config && set && set.sku) this.rememberSku(context, config, set.sku);
      setTimeout(() => {
        if (window.FermentationCarouselPicker && typeof window.FermentationCarouselPicker.selectSetBySku === 'function') {
          window.FermentationCarouselPicker.selectSetBySku(set.sku, true);
        }
      }, 100);
    },

    intelligentInit: function(ctx) {
      const context = ctx || this.getContext();
      if (!context.container) return;

      const config = Shared.parseConfig(context.container);
      if (!config) return;
      if (!this.hasFixedSets(config)) {
        this.initializeCustom(context);
        this.showCustomizer(context);
        return;
      }

      const currentVariant = this.getCurrentVariant(context);
      if (currentVariant && currentVariant.sku) this.rememberSku(context, config, currentVariant.sku);
      const matchingFixedSet = this.findFixedSetByVariant(config, currentVariant);

      if (matchingFixedSet || !currentVariant) {
        this.showCarousel(context);
        this.ensureCarouselAssetsLoaded()
          .then(() => {
            const now = this.getContext();
            this.initializeCarousel(now);
            if (matchingFixedSet) {
              this.showCarouselWithSet(matchingFixedSet, now);
            } else if (window.FermentationCarouselPicker && typeof window.FermentationCarouselPicker.selectDefaultSet === 'function') {
              window.FermentationCarouselPicker.selectDefaultSet(false);
            }
          })
          .catch(() => {
            this.initializeCustom(context);
            this.showCustomizer(context);
          });
        return;
      }

      this.initializeCustom(context);
      this.showCustomizer(context);
      if (window.FermentationPicker && typeof window.FermentationPicker.updateUI === 'function') {
        window.FermentationPicker.updateUI(false);
      }
    },

    switchToCustom: function() {
      const ctx = this.getContext();
      if (!ctx.container) return;
      const config = Shared.parseConfig(ctx.container);
      if (!config) return;

      const targetCustomSku = this.resolveTargetCustomSku(ctx, config);
      this.initializeCustom(ctx);
      this.showCustomizer(ctx);

      if (targetCustomSku) {
        Shared.updateVariantBySku(targetCustomSku, ctx.root);
        this.rememberSku(ctx, config, targetCustomSku);
      }

      if (window.FermentationPicker && typeof window.FermentationPicker.updateUI === 'function') {
        window.FermentationPicker.updateUI(false, ctx.root);
      }
    },

    switchToCarousel: function() {
      const ctx = this.getContext();
      if (!ctx.container) return;

      const config = Shared.parseConfig(ctx.container);
      if (!config) return;
      if (!this.hasFixedSets(config)) {
        this.switchToCustom();
        return;
      }

      const finishSwitch = () => {
        const targetCarouselSku = this.resolveTargetCarouselSku(ctx, config);

        this.showCarousel(ctx);
        if (
          targetCarouselSku &&
          window.FermentationCarouselPicker &&
          typeof window.FermentationCarouselPicker.selectSetBySku === 'function'
        ) {
          window.FermentationCarouselPicker.selectSetBySku(targetCarouselSku, false);
        } else if (
          window.FermentationCarouselPicker &&
          typeof window.FermentationCarouselPicker.selectDefaultSet === 'function'
        ) {
          window.FermentationCarouselPicker.selectDefaultSet(false);
        }
        this.rememberSku(ctx, config, targetCarouselSku);
      };

      if (!window.FermentationCarouselPicker) {
        this.ensureCarouselAssetsLoaded()
          .then(() => {
            const now = this.getContext();
            const nowConfig = now.container ? Shared.parseConfig(now.container) : null;
            if (!this.hasFixedSets(nowConfig)) {
              this.switchToCustom();
              return;
            }
            this.initializeCarousel(now);
            finishSwitch();
          })
          .catch(() => this.switchToCustom());
        return;
      }

      this.initializeCarousel(ctx);
      finishSwitch();
    },

    setupSwitching: function(ctx) {
      const container = ctx && ctx.container ? ctx.container : this.getContext().container;
      if (!container) return;
      if (container.dataset.fermentationMergedSwitchingBound === 'true') return;
      container.dataset.fermentationMergedSwitchingBound = 'true';

      const backToCustomButton = container.querySelector('#back-to-custom');
      if (backToCustomButton && !backToCustomButton.dataset.fermentationSwitchBound) {
        backToCustomButton.dataset.fermentationSwitchBound = 'true';
        backToCustomButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.switchToCarousel();
        });
      }

      const showCustomLink = container.querySelector('#show-custom-link');
      if (showCustomLink && !showCustomLink.dataset.fermentationSwitchBound) {
        showCustomLink.dataset.fermentationSwitchBound = 'true';
        showCustomLink.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.switchToCustom();
        });
      }

      container.addEventListener('click', (event) => {
        const showCustomButton = event.target.closest('[data-show-custom]');
        if (showCustomButton && container.contains(showCustomButton)) {
          event.preventDefault();
          this.switchToCustom();
          return;
        }

        const showCarouselButton = event.target.closest('[data-show-carousel]');
        if (showCarouselButton && container.contains(showCarouselButton)) {
          event.preventDefault();
          this.switchToCarousel();
        }
      });
    }
  };

  const initializeMergedPicker = function() {
    if (!getMergedContainer(getScopeRoot())) return;
    if (window.fermentationMergedPicker && typeof window.fermentationMergedPicker.init === 'function') {
      window.fermentationMergedPicker.init();
    }
  };

  if (document.readyState !== 'loading') {
    initializeMergedPicker();
  } else {
    document.addEventListener('DOMContentLoaded', initializeMergedPicker);
  }

  document.addEventListener('quickview:loaded', initializeMergedPicker);
  document.addEventListener('product-info:loaded', initializeMergedPicker);
})();
